import type { TFunction } from 'i18next';
import { z } from 'zod';

import { createValidationMessages } from '@/shared/libs';
import type { Provider, ProviderInput } from '@psp-aggregator/sdk';

const stringList = z.array(z.string());

export const createProviderFormSchema = (t: TFunction) => {
  const messages = createValidationMessages(t);

  return z.object({
    alias: z.string().optional(),
    contact_email: z.string().optional(),
    contact_name: z.string().optional(),
    contact_phone: z.string().optional(),
    countries: stringList,
    currencies: stringList,
    full_description: z.string().max(5000).optional(),
    gallery_urls: stringList,
    industries: stringList,
    integration_methods: stringList,
    jurisdictions: stringList,
    licenses: z.array(
      z.object({
        document_url: z.string().optional(),
        name: z.string(),
      }),
    ),
    linkedin: z.string().optional(),
    logo_url: z.string().optional(),
    messengers: stringList,
    payment_methods: stringList,
    real_name: z.string().min(1, { message: messages.required() }),
    regulators: stringList,
    short_description: z.string().max(500).optional(),
    social_links: stringList,
    website: z.string().optional(),
  });
};

export type ProviderFormValues = z.infer<
  ReturnType<typeof createProviderFormSchema>
>;

export const PROVIDER_FORM_DEFAULT_VALUES: ProviderFormValues = {
  alias: '',
  contact_email: '',
  contact_name: '',
  contact_phone: '',
  countries: [],
  currencies: [],
  full_description: '',
  gallery_urls: [],
  industries: [],
  integration_methods: [],
  jurisdictions: [],
  licenses: [],
  linkedin: '',
  logo_url: '',
  messengers: [],
  payment_methods: [],
  real_name: '',
  regulators: [],
  short_description: '',
  social_links: [],
  website: '',
};

export const providerToFormValues = (
  provider: Provider,
): ProviderFormValues => ({
  alias: provider.alias ?? '',
  contact_email: provider.contacts?.email ?? '',
  contact_name: provider.contacts?.contact_name ?? '',
  contact_phone: provider.contacts?.phone ?? '',
  countries: provider.countries ?? [],
  currencies: provider.currencies ?? [],
  full_description: provider.full_description ?? provider.description ?? '',
  gallery_urls: provider.gallery_urls ?? [],
  industries: provider.industries ?? [],
  integration_methods: provider.integration_methods ?? [],
  jurisdictions: provider.jurisdictions ?? [],
  licenses: (provider.licenses ?? []).map((license) => ({
    document_url: license.document_url ?? '',
    name: license.name,
  })),
  linkedin: provider.contacts?.linkedin ?? '',
  logo_url: provider.logo_url ?? '',
  messengers: provider.contacts?.messengers ?? [],
  payment_methods: provider.payment_methods ?? [],
  real_name: provider.real_name,
  regulators: provider.regulators ?? [],
  short_description: provider.short_description ?? '',
  social_links: provider.contacts?.social_links ?? [],
  website: provider.contacts?.website ?? '',
});

export const formValuesToProviderInput = (
  values: ProviderFormValues,
): ProviderInput => ({
  alias: values.alias?.trim() || undefined,
  contacts: {
    contact_name: values.contact_name?.trim() || undefined,
    email: values.contact_email?.trim() || undefined,
    linkedin: values.linkedin?.trim() || undefined,
    messengers: values.messengers.length > 0 ? values.messengers : undefined,
    phone: values.contact_phone?.trim() || undefined,
    social_links:
      values.social_links.length > 0 ? values.social_links : undefined,
    website: values.website?.trim() || undefined,
  },
  countries: values.countries,
  currencies: values.currencies,
  full_description: values.full_description?.trim() || undefined,
  gallery_urls: values.gallery_urls,
  industries: values.industries,
  integration_methods: values.integration_methods,
  jurisdictions: values.jurisdictions,
  licenses: values.licenses
    .filter((license) => license.name.trim())
    .map((license) => ({
      document_url: license.document_url?.trim() || undefined,
      name: license.name.trim(),
    })),
  logo_url: values.logo_url?.trim() || undefined,
  payment_methods: values.payment_methods,
  real_name: values.real_name.trim(),
  regulators: values.regulators,
  short_description: values.short_description?.trim() || undefined,
});
